<template>
  <div id="event-plan-details">
    <v-card class="plan-card" elevation="16">
      <!-- Header Section with Gradient -->
      <div class="plan-header">
        <div class="header-gradient-bg"></div>
        <div class="header-backdrop"></div>
        <div class="header-content">
          <div class="header-icon-box">
            <v-icon large color="white">mdi-tag-multiple</v-icon>
          </div>
          <div class="header-text">
            <h1 class="plan-name">{{ Plan.PlanName }}</h1>
            <p class="plan-price">
              {{ Plan.SubTotalTxt }} <span class="price-label">per pax</span>
            </p>
          </div>
          <v-btn
            icon
            absolute
            right
            top
            color="white"
            class="close-btn"
            @click.prevent="closePrompt"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Content Section -->
      <v-card-text class="plan-content">
        <!-- Plan Details Icon Row -->
        <div class="content-header">
          <v-icon small color="primary" class="mr-2"
            >mdi-information-outline</v-icon
          >
          <h3 class="content-title">Plan Details</h3>
        </div>

        <!-- Plan Description -->
        <div class="plan-description">
          <div v-html="Plan.Content" class="description-text" />
        </div>

        <!-- Features/Benefits Section (if needed) -->
        <div class="plan-features" v-if="Plan.Features">
          <v-divider class="my-6"></v-divider>
          <div class="content-header">
            <v-icon small color="success" class="mr-2"
              >mdi-check-circle-outline</v-icon
            >
            <h3 class="content-title">What's Included</h3>
          </div>
          <div v-html="Plan.Features" class="features-text" />
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Action Footer -->
      <v-card-actions class="action-footer">
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          large
          rounded
          elevation="6"
          class="close-btn-action"
          @click.prevent="closePrompt"
        >
          <v-icon left>mdi-close-circle-outline</v-icon>
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
export default {
  props: {
    Plan: {
      required: true,
    },
  },
  data() {
    return {};
  },
  methods: {
    closePrompt() {
      console.log("closePrompt called");
      this.$emit("hideDialog");
    },
  },
};
</script>

<style lang="scss" scoped>
// Color Palette
$primary-color: #1f49c5;
$secondary-color: #0097d7;
$accent-gold: #ffd700;
$gradient-main: linear-gradient(
  135deg,
  #001748 0%,
  #1f49c5 50%,
  #00338f 75%,
  #0097d7 100%
);
$success-color: #10b981;
$text-primary: #1a202c;
$text-secondary: #555;
$border-color: #e2e8f0;
$bg-light: #f7f9fc;

// Animation Keyframes
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

#event-plan-details {
  .plan-card {
    border-radius: 16px !important;
    overflow: hidden;
    border: none;
    animation: slideDown 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 20px 60px rgba(102, 126, 234, 0.3) !important;

    // ============================================
    // HEADER SECTION
    // ============================================
    .plan-header {
      background: $gradient-main;
      position: relative;
      overflow: hidden;
      padding: 32px 24px;
      min-height: 140px;

      .header-gradient-bg {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: $gradient-main;
        opacity: 1;
      }

      .header-backdrop {
        position: absolute;
        top: -50%;
        right: -50%;
        width: 200%;
        height: 200%;
        background: radial-gradient(
          circle,
          rgba(255, 255, 255, 0.1) 0%,
          transparent 70%
        );
        animation: rotate 20s linear infinite;
      }

      .header-content {
        position: relative;
        z-index: 1;
        display: flex;
        align-items: center;
        gap: 20px;
      }

      .header-icon-box {
        background: rgba(255, 255, 255, 0.2);
        backdrop-filter: blur(10px);
        border-radius: 14px;
        padding: 12px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
        animation: float 3s ease-in-out infinite;
        flex-shrink: 0;
      }

      .header-text {
        flex: 1;
        animation: slideDown 0.8s ease-out;

        .plan-name {
          font-size: 24px;
          font-weight: 800;
          color: white;
          margin: 0;
          letter-spacing: 0.5px;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
        }

        .plan-price {
          font-size: 18px;
          color: rgba(255, 255, 255, 0.95);
          margin: 8px 0 0 0;
          font-weight: 600;
          letter-spacing: 0.3px;

          .price-label {
            font-size: 12px;
            opacity: 0.9;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-weight: 500;
          }
        }
      }

      .close-btn {
        position: absolute !important;
        right: 12px !important;
        top: 12px !important;
        background: rgba(255, 255, 255, 0.15) !important;
        backdrop-filter: blur(10px);
        border: 2px solid rgba(255, 255, 255, 0.3);
        transition: all 0.3s ease;

        &:hover {
          background: rgba(255, 255, 255, 0.25) !important;
          transform: rotate(90deg);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2) !important;
        }
      }
    }

    // ============================================
    // CONTENT SECTION
    // ============================================
    .plan-content {
      padding: 32px;
      background: linear-gradient(135deg, #ffffff 0%, #f8f5ff 100%);
      animation: slideInUp 0.8s ease-out 0.2s backwards;

      .content-header {
        display: flex;
        align-items: center;
        margin-bottom: 16px;

        .content-title {
          font-size: 16px;
          font-weight: 700;
          color: $text-primary;
          margin: 0;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }

      .plan-description {
        background: white;
        border-radius: 12px;
        padding: 20px;
        border-left: 4px solid $primary-color;
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
        transition: all 0.3s ease;
        animation: fadeIn 0.6s ease-out 0.3s backwards;

        &:hover {
          box-shadow: 0 8px 24px rgba(102, 126, 234, 0.15);
          transform: translateX(4px);
        }

        .description-text {
          color: $text-secondary;
          font-size: 14px;
          line-height: 1.8;
          letter-spacing: 0.3px;

          ::v-deep {
            p {
              margin: 0 0 12px 0;

              &:last-child {
                margin-bottom: 0;
              }
            }

            ul,
            ol {
              margin: 12px 0;
              padding-left: 24px;

              li {
                margin-bottom: 8px;
                color: $text-secondary;

                &:last-child {
                  margin-bottom: 0;
                }
              }
            }

            strong {
              color: $text-primary;
              font-weight: 700;
            }

            em {
              color: $primary-color;
              font-style: italic;
            }
          }
        }
      }

      .plan-features {
        animation: fadeIn 0.6s ease-out 0.4s backwards;

        .features-text {
          background: linear-gradient(
            135deg,
            rgba(16, 185, 129, 0.08) 0%,
            rgba(5, 150, 105, 0.08) 100%
          );
          border-radius: 12px;
          padding: 20px;
          border-left: 4px solid $success-color;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.1);
          color: $text-secondary;
          font-size: 14px;
          line-height: 1.8;
          letter-spacing: 0.3px;
          transition: all 0.3s ease;

          &:hover {
            box-shadow: 0 8px 24px rgba(16, 185, 129, 0.15);
            transform: translateX(4px);
          }

          ::v-deep {
            p {
              margin: 0 0 12px 0;

              &:last-child {
                margin-bottom: 0;
              }
            }

            ul,
            ol {
              margin: 12px 0;
              padding-left: 24px;

              li {
                margin-bottom: 8px;

                &:last-child {
                  margin-bottom: 0;
                }
              }
            }

            strong {
              color: $text-primary;
              font-weight: 700;
            }
          }
        }
      }
    }

    // ============================================
    // ACTION FOOTER
    // ============================================
    .action-footer {
      padding: 20px 32px;
      background: $bg-light;
      border-top: 1px solid $border-color;
      animation: slideInUp 0.8s ease-out 0.3s backwards;

      .close-btn-action {
        background: $gradient-main !important;
        color: white !important;
        font-weight: 700 !important;
        letter-spacing: 0.5px !important;
        text-transform: uppercase !important;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3) !important;

        &:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4) !important;

          .v-icon {
            transform: rotate(90deg);
          }
        }

        .v-icon {
          transition: all 0.3s ease;
        }
      }
    }

    .my-6 {
      margin: 24px 0;
    }

    .mr-2 {
      margin-right: 8px;
    }
  }
}

// ============================================
// RESPONSIVE DESIGN
// ============================================
@media (max-width: 960px) {
  #event-plan-details {
    .plan-card {
      .plan-header {
        padding: 24px;
        min-height: auto;

        .header-content {
          flex-wrap: wrap;
          gap: 12px;
        }

        .header-text {
          .plan-name {
            font-size: 20px;
          }

          .plan-price {
            font-size: 16px;
          }
        }
      }

      .plan-content {
        padding: 24px;
      }

      .action-footer {
        padding: 16px 24px;
      }
    }
  }
}

@media (max-width: 600px) {
  #event-plan-details {
    .plan-card {
      border-radius: 12px !important;

      .plan-header {
        padding: 16px;
        min-height: auto;

        .header-content {
          flex-direction: column;
          align-items: flex-start;
          gap: 12px;
        }

        .header-icon-box {
          width: 40px;
          height: 40px;
          padding: 8px;

          ::v-deep .v-icon {
            font-size: 24px !important;
          }
        }

        .header-text {
          width: 100%;

          .plan-name {
            font-size: 18px;
          }

          .plan-price {
            font-size: 14px;
          }
        }

        .close-btn {
          width: 36px !important;
          height: 36px !important;
          right: 8px !important;
          top: 8px !important;
        }
      }

      .plan-content {
        padding: 16px;

        .content-header {
          .content-title {
            font-size: 14px;
          }
        }

        .plan-description,
        .plan-features .features-text {
          padding: 16px;
          font-size: 13px;
        }
      }

      .action-footer {
        padding: 12px 16px;
        flex-direction: column;

        .close-btn-action {
          width: 100%;
          margin-top: 12px;
        }
      }
    }
  }
}
</style>